import * as d3 from 'd3';

import { IOverlayModel } from './IOverlayModel';
import { Qualities } from '../../helpers/helpers';
import { greyBackgroundColor } from '../../../common/constants';
import { getRandomInt32 } from '../../../common/helpers/getRandom';

export class OverlaySeries {
    private _dispatch: any;
    private _drag: d3.behavior.Drag<any>;
    private _dragResize: d3.behavior.Drag<any>;
    private _group: d3.Selection<any>;
    private _handleSize = 8;
    private _isReadOnly: boolean;
    private _randomId = getRandomInt32();
    private _viewPortCenter: [number, number];
    private _xScale: d3.scale.Linear<number, number>;
    private _yScale: d3.scale.Linear<number, number>;
    private _zoomScale = 1;

    constructor(selection: d3.Selection<any>, isReadOnly: boolean) {
        const self = this;
        this._isReadOnly = isReadOnly;

        this._group = selection.append('g')
            .classed('overlay-series', true);

        const filter = this._group
            .append('defs')
            .append('filter')
            .attr({
                'id': `blur_${this._randomId}`,
                'x': -2,
                'y': -2,
                'width': '10',
                'height': '10'
            });

        filter.append('feGaussianBlur')
            .attr({
                'result': 'blurOut',
                'stdDeviation': '10'
            });

        filter.append('feFlood')
            .attr({
                'flood-color': 'orange',
                'result': 'offsetColor',
            });

        filter.append('feComposite')
            .attr({
                'in': 'offsetColor',
                'in2': 'blurOut',
                'operator': 'in',
                'result': 'offsetBlur',
            });

        filter.append('feBlend')
            .attr({
                'in': 'SourceGraphic',
                'in2': 'offsetBlur',
                'mode': 'normal'
            });

        this._drag = d3.behavior.drag()
            .on('dragstart', (d: IOverlayModel) => {
                (d3.event as any).sourceEvent.stopPropagation();
            })
            .on('dragend', (d: IOverlayModel) => {
                if (d.dragend) {
                    d.dragend();
                }
            })
            .on('drag', function (d: IOverlayModel) {
                if (!self.isReadOnly()) {
                    //const mousevent = (<any>d3.event).sourceEvent;
                    const mousevent = d3.event as any;
                    const sel = d3.select(this);
                    const transform = d3.transform(sel.attr('transform'));
                    const x = transform.translate[0] + mousevent.dx;
                    const y = transform.translate[1] + mousevent.dy;
                    d.position.x = x;
                    d.position.y = y;
                    sel.attr({ 'transform': `translate(${x},${y})` });
                }
            });

        this._dragResize = d3.behavior.drag()
            .on('dragstart', () => (d3.event as any).sourceEvent.stopPropagation())
            .on('dragend', (d: IOverlayModel) => {
                if (d.dragend) {
                    d.dragend();
                }
            })
            .on('drag', function (d: IOverlayModel) {
                if (!self.isReadOnly()) {
                    //const mousevent = (<any>d3.event).sourceEvent;
                    const mousevent = d3.event as any;
                    const sel = d3.select(this);
                    const rect = d3.select(this.previousSibling).select('rect');
                    const labels = d3.select(this.previousSibling).select('.labels');
                    const w = Math.max(20, d.size.width + mousevent.dx);
                    const h = Math.max(20, d.size.height + mousevent.dy);
                    d.size.width = w;
                    d.size.height = h;
                    rect.attr({
                        'width': w,
                        'height': h
                    });
                    d3.select(this.parentNode)
                        .select('defs')
                        .select('rect')
                        .attr('width', w)
                        .attr('height', h);
                    sel.attr('transform',
                        e => `translate(${e.size.width - self._handleSize - 0.5}, ${e.size.height - self._handleSize
                            - 0.5})`
                    );

                    labels.selectAll('.align-right').attr('x', e => e.size.width - 10);
                }
            });

        this._dispatch = d3.dispatch('selected', 'hovered', 'resized');
        //this._isReadOnly = true;
    }

    update(data: IOverlayModel[]) {
        const self = this;
        const zoomScale = this._zoomScale ? this._zoomScale : 1;

        this._group
            .selectAll('.serie')
            .remove();

        const dataBound = this._group
            .selectAll('.serie')
            .data(data);

        dataBound.exit()
            .remove();

        const enterSerieSelection = dataBound.enter()
            .append('g')
            .classed('serie', true)
            .classed('clickable', d => !!d.click)
            .each(d => {
                if (!(d.position && d.position.x && d.position.y) && self._viewPortCenter) {
                    // adding center of rect at center of viewport
                    [d.position.x, d.position.y] = [
                        self._viewPortCenter[0] - d.size.width / 2,
                        self._viewPortCenter[1] - d.size.height / 2
                    ];
                }
            })
            .on('mouseenter', d => this._dispatch.hovered(d))
            .on('click', d => {
                this._dispatch.selected(d);
                this.doClick(d, true);
            })
            .call(this._drag);

        const clippathId = `clippath${getRandomInt32()}_`;
        enterSerieSelection.append('defs')
            .append('clipPath')
            .attr('id', (d, i) => clippathId + i)
            .append('rect')
            .attr({
                'width': d => d.size.width,
                'height': d => d.size.height
            })
            .classed('scalable', true);

        const enterSelection = enterSerieSelection
            .append('g')
            .style('clip-path', (d, i) => `url(#${clippathId + i})`);

        // add the dashboard name as title for every series
        enterSerieSelection
            .filter(d => !!d.dashboardName)
            .append('title')
            .text(d => d.dashboardName);

        this.setLink(enterSerieSelection);

        dataBound
            .attr('transform', d => `translate(${d.position.x}, ${d.position.y})`)
            .classed('selected', d => d.selected)
            .attr('filter', d => d.selected ? `url(#blur_${this._randomId})` : null);

        enterSelection.append('rect')
            .classed('series-background', true)
            .classed('scalable', true);

        dataBound.select('clipPath')
            .select('rect')
            .attr({
                'width': d => d.size.width,
                'height': d => d.size.height
            });

        dataBound.select('rect.series-background')
            .attr({
                'width': d => d.size.width,
                'height': d => d.size.height
            })
            .style({
                'stroke': d => d.borderColor,
                'fill': d => d.backgroundColor
            });

        const labels = enterSelection.append('g')
            .classed('labels', true)
            .classed('with-popover', true)
            .on('click', d => this.doClick(d, false));

        dataBound.selectAll('g.with-popover')
            .each(function (d) {
                d3.select(this).attr(d.attributes);
            });

        labels.append('text')
            .classed('name', true)
            .style('text-anchor', 'start')
            .attr({
                'x': 0,
                'y': 10 * zoomScale
            });
        labels.append('text')
            .classed('value', true)
            .classed('line-two', true)
            .style('text-anchor', 'start')
            .attr({
                'x': 0
            });

        labels.append('text')
            .classed('quality', true)
            .classed('line-two', true)
            .classed('fa', true)
            .classed('fa-fw', true)
            .classed('align-right', true)
            .style('text-anchor', 'end')
            .attr('x', 0);

        dataBound.select('text.name')
            .style('fill', d => d.textColor)
            .style('visibility', d => d.name ? 'visible' : 'hidden')
            .text(d => d.name);
        
        dataBound.select('text.value')
            .style('fill', d => d.textColor)
            .attr('y', d => (d.name ? 30 : 10) * zoomScale)
            .text(d => d.value);

        dataBound.select('text.quality')
            .style('fill', d => Qualities.getColor(d.quality))
            .attr('y', d => (d.name ? 30 : 10) * zoomScale)
            .attr('x', d => d.size.width - 10)
            .html(d => Qualities.getIcon(d.quality));

        dataBound.selectAll('text')
            .attr('transform', `scale(${1 / zoomScale})`);

        enterSerieSelection.append('path')
            .attr('d', `M 0 ${this._handleSize} ${this._handleSize} 0 ${this._handleSize} ${this._handleSize}Z`)
            .classed('handle', true)
            .attr('cursor', 'nwse-resize')
            .attr('fill', greyBackgroundColor)
            .attr('fill-opacity', '.5')
            .attr('transform', d =>
                `translate(${d.size.width - this._handleSize - 0.5}, ${d.size.height - this._handleSize - 0.5})`)
            .call(this._dragResize);

        this.refreshSeries();
    }

    setLink(enterSerieSelection: d3.Selection<IOverlayModel>) {
        // add fa-icon on serie with icons
        enterSerieSelection
            .filter(d => d.showIcon && d.dashboardGuid !== undefined)
            .append('g')
            .attr('transform', d => `translate(${d.size.width - 6}, 6)`)
            .append('text')
            .classed('fa-icon', true)
            .text(d => d.icon) // fa-th-large or fa-check-square-o
            .on('click', d => this.doClick(d, true))
            .append('title')
            .text(d => d.dashboardName);
    }

    refreshSeries() {
        this._group
            .selectAll('path.handle')
            .style('display', this._isReadOnly ? 'none' : 'inherit');
    }

    updateZoomScale(scale: number, c: [number, number]) {
        scale = isNaN(scale) ? 1 : scale;
        this._zoomScale = scale;
        this._viewPortCenter = c;
        this._group
            .selectAll('text')
            //scale text to opposite scaling level to keep constant font size
            .attr('transform', `scale(${1 / scale})`);
        this._group.selectAll('text.name')
            .attr('y', 10 * scale);
        const labels = this._group
            .selectAll('.labels');
        labels.select('text.quality')
            .attr('y', d => (d.name ? 30 : 10) * scale);
        labels.select('text.value')
            .attr('y', d => (d.name ? 30 : 10) * scale);
    }

    isReadOnly(value?: boolean) {
        if (arguments.length) {
            this._isReadOnly = value;
            this.refreshSeries();
        }
        return this._isReadOnly;
    }

    on(value: string, callback: any) {
        this._dispatch.on(value, (d) => callback(d));
    }

    xScale(scale: d3.scale.Linear<number, number>) {
        this._xScale = scale;
    }

    yScale(scale: d3.scale.Linear<number, number>) {
        this._yScale = scale;
    }

    private doClick(d: IOverlayModel, toDashboard: boolean) {
        if (d.click) {
            d.click(d, toDashboard);
            (d3.event as any).stopPropagation();
        }
    }
}